/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import {
    ICheckoutDeliveryOption,
    ICheckoutDeliveryOptionEdit,
    ICheckoutDeliveryOptionsList,
    ICheckoutDeliveryOptionsViewProps
} from './dlvry-checkout-v2-delivery-options';
import DlvryCheckoutDeliveryDateComponent from './components/dlvry-checkout-v2-delivery-date';

const DeliveryOptionList: React.FC<ICheckoutDeliveryOptionsList> = ({ DeliveryOptionsList, list }) => {
    if (!list || list.length === 0) {
        return null;
    }

    return (
        <Node {...DeliveryOptionsList}>
            {list.map((deliveryOption: ICheckoutDeliveryOptionEdit) => {
                return (
                    <Node {...deliveryOption.DeliveryOption} key={deliveryOption.code} aria-label='Please select a delivery option.'>
                        <Node {...deliveryOption.deliveryOptionWrapper}>
                            {deliveryOption.icon}
                            <div style={{ flex: 1, minWidth: 0 }}>
                                {deliveryOption.description}
                                {deliveryOption.subDescription}
                            </div>
                            {deliveryOption.price}
                            {deliveryOption.radioButton}
                        </Node>
                    </Node>
                );
            })}
        </Node>
    );
};

const DeliveryOptionSelected: React.FC<ICheckoutDeliveryOption> = ({
    DeliveryOption,
    description,
    icon,
    subDescription,
    price
}) => {
    return (
        <Node {...DeliveryOption}>
            {icon}
            <div style={{ flex: 1, minWidth: 0 }}>
                {description}
                {subDescription}
            </div>
            {price}
        </Node>
    );
};

const CheckoutDeliveryOptionsView: React.FC<ICheckoutDeliveryOptionsViewProps> = props => {
    const {
        CheckoutDeliveryOptions,
        enableCheckoutCartLineDeliveryOptions,
        checkoutErrorRef,
        viewState,
        subHeader,
        deliveryOptions,
        deliveryOptionSelected,
        alert,
        waiting,
        saveButton,
        editButton,
        cancelButton,
        checkoutCartLineGroupDeliveryOptions,
        deliveryDateProps
    } = props;

    return (
        // TODO: All wrapper div should be provided by viewProps. Once SDK provide the sln, update markup.
        <Module {...CheckoutDeliveryOptions} ref={checkoutErrorRef}>
            {subHeader}
            {viewState.isLoading && waiting}
            {viewState.isError && alert}
            <Node className='ms-checkout-delivery-options__content'>
                <Node className='ms-checkout-delivery-options__list__wrapper'>
                    <p className='ms-checkout-delivery-options__list__wrapper__label'>
                        {props.resources.chooseDeliveryModeLabel}
                    </p>
                    {viewState.isShowList && deliveryOptions && !enableCheckoutCartLineDeliveryOptions && (
                        <DeliveryOptionList {...deliveryOptions} />
                    )}
                    {viewState.isShowList && enableCheckoutCartLineDeliveryOptions && checkoutCartLineGroupDeliveryOptions}
                    {viewState.isShowSelected && deliveryOptionSelected && <DeliveryOptionSelected {...deliveryOptionSelected} />}
                </Node>
                <Node className='ms-checkout-delivery-options__deliverydate__wrapper'>
                    <p className='ms-checkout-delivery-options__deliverydate__wrapper__label'>
                        {props.resources.chooseDeliveryDateLabel}
                    </p>
                    <DlvryCheckoutDeliveryDateComponent {...deliveryDateProps} />
                    {!deliveryDateProps.hasDeliveryDates && (
                        <div className='ms-checkout-delivery-options__no-delivery-dates'>
                            {props.resources.noDeliveryDatesErrorText}
                        </div>
                    )}
                </Node>
            </Node>
            {viewState.isShowSaveButton && saveButton}
            {viewState.isShowEditButton && editButton}
            {viewState.isShowCancelButton && cancelButton}
        </Module>
    );
};

export default CheckoutDeliveryOptionsView;
